/**
 * KPIs por usuario: logins y aprobaciones.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { dataLabelsPlugin } from '../../chartPlugins';
import StatsDetailModal from './StatsDetailModal';
import { ink, studio, SearchField, FilterDock, FilterPill, FilterDate, FilterMeta, StudioTabs, RoleBadge } from './studioUi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  dataLabelsPlugin,
  Filler
);

const CHART = {
    blue: '#0288d1',
    blueFill: 'rgba(2, 136, 209, 0.18)',
    purple: '#5E35B1',
    purpleFill: 'rgba(94, 53, 177, 0.62)',
    green: '#137333',
    greenFill: 'rgba(19, 115, 51, 0.62)',
    orange: '#e65100',
    orangeFill: 'rgba(230, 81, 0, 0.62)',
    danger: '#c5221f',
    dangerFill: 'rgba(197, 34, 31, 0.62)',
    pink: 'rgba(233, 30, 99, 0.62)',
};

const lastLoginMs = (timestamp) => {
    if (!timestamp) return null;
    const date = timestamp.toDate
        ? timestamp.toDate()
        : timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
    const ms = date.getTime();
    return Number.isNaN(ms) ? null : ms;
};

const mean = (items, pick) => {
    if (!items.length) return 0;
    return items.reduce((sum, item) => sum + (Number(pick(item)) || 0), 0) / items.length;
};

const fmtAvg = (value, digits = 1) => Number(value || 0).toFixed(digits);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const scoreTone = (score) => {
    if (score >= 80) return CHART.greenFill;
    if (score >= 60) return CHART.orangeFill;
    return CHART.dangerFill;
};

const groupByArea = (people) => {
    const map = new Map();
    people.forEach((user) => {
        const key = user.area || 'Sin área';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(user);
    });
    return [...map.entries()].map(([name, group]) => {
        const assigned = group.reduce((sum, user) => sum + (user.reportsAssigned || 0), 0);
        const approved = group.reduce((sum, user) => sum + (user.reportsApproved || 0), 0);
        const pending = group.reduce((sum, user) => sum + (user.reportsPending || 0), 0);
        const late = group.reduce((sum, user) => sum + (user.lateApprovalCount || 0), 0);
        const falseApprovals = group.reduce((sum, user) => sum + (user.falseApprovalCount || 0), 0);
        const adjAssigned = group.reduce((sum, user) => sum + (user.adjustmentsAssigned || 0), 0);
        const adjPending = group.reduce((sum, user) => sum + (user.adjustmentsPending || 0), 0);
        const adjOpen = group.reduce((sum, user) => sum + (user.adjustmentsOpen || 0), 0);
        const adjIgnored = group.reduce((sum, user) => sum + (user.adjustmentsIgnored || 0), 0);
        const adjResolved = group.reduce((sum, user) => sum + (user.adjustmentsResolved || 0), 0);
        const approvalRate = assigned > 0 ? (approved / assigned) * 100 : 0;
        const pendingRate = assigned > 0 ? (pending / assigned) * 100 : 0;
        const lateRate = assigned > 0 ? (late / assigned) * 100 : 0;
        const falseRate = assigned > 0 ? (falseApprovals / assigned) * 100 : 0;
        const index = clamp(
            approvalRate * 0.5
            + Math.max(0, 100 - lateRate) * 0.2
            + Math.max(0, 100 - falseRate) * 0.2
            + Math.max(0, 100 - pendingRate) * 0.1,
            0,
            100
        );
        const totalLogins = group.reduce((sum, user) => sum + (user.totalLogins || 0), 0);
        const withLogin = group.filter((user) => lastLoginMs(user.lastLogin) != null);
        const now = Date.now();
        return {
            name,
            people: group.length,
            assigned,
            approvalRate,
            late,
            falseApprovals,
            lateAvg: mean(group, (user) => user.lateApprovalCount),
            falseAvg: mean(group, (user) => user.falseApprovalCount),
            adjustmentsAssigned: adjAssigned,
            adjustmentsPending: adjPending,
            adjustmentsOpen: adjOpen,
            adjustmentsIgnored: adjIgnored,
            adjustmentResolveRate: adjAssigned > 0 ? (adjResolved / adjAssigned) * 100 : 0,
            index,
            totalLogins,
            avgLogins: mean(group, (user) => user.totalLogins),
            neverLogged: group.length - withLogin.length,
            idleDays: mean(withLogin, (user) => (now - lastLoginMs(user.lastLogin)) / 86400000),
        };
    });
};

const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Nunca';
    const date = timestamp.toDate
        ? timestamp.toDate()
        : timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const rateTone = (rate) => {
    if (rate >= 95) return { color: '#137333', backgroundColor: '#e6f4ea' };
    if (rate >= 80) return { color: '#e65100', backgroundColor: '#fff3e0' };
    return { color: ink.danger, backgroundColor: ink.dangerSoft };
};

const RateChip = ({ value }) => (
    <span style={{ ...ui.chip, ...rateTone(value) }}>{Number(value || 0).toFixed(1)}%</span>
);

const CountChip = ({ value, warn }) => (
    <span style={{ ...ui.chip, ...(warn && value > 0 ? { color: ink.danger, backgroundColor: ink.dangerSoft } : {}) }}>
        {value || 0}
    </span>
);

const UserStatisticsPanel = ({ styles, fetchWithAuth, API_URL, users, areasList }) => {
    const [loginStats, setLoginStats] = useState({ timeSeries: [], totalLogins: 0, uniqueUsersCount: 0 });
    const [topUsers, setTopUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        empresa: '',
        area: '',
        startDate: '',
        endDate: '',
    });
    
    // State for the new functionality
    const [view, setView] = useState('general'); // 'general' or 'user'
    const [selectedUser, setSelectedUser] = useState(null);
    const [userSpecificStats, setUserSpecificStats] = useState(null);
    const [loadingUserStats, setLoadingUserStats] = useState(false);
    const [userFilters, setUserFilters] = useState({ empresa: '', searchTerm: '' });
    const [areaFilters, setAreaFilters] = useState({ empresa: '', area: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'ascending' });
    const [modalData, setModalData] = useState(null);
    const [hoveredKpi, setHoveredKpi] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [loadingModal, setLoadingModal] = useState(false);

    // NEW state for the user list with stats
    const [usersWithStats, setUsersWithStats] = useState([]);
    const [loadingUsersWithStats, setLoadingUsersWithStats] = useState(false);

    // Get unique companies from users list
    const companyList = useMemo(() => {
        if (!users) return [];
        const companies = users.map(u => u.empresa).filter(Boolean);
        return [...new Set(companies)];
    }, [users]);

    const directoryByUid = useMemo(() => {
        const map = new Map();
        (users || []).forEach((person) => map.set(person.uid, person));
        return map;
    }, [users]);

    const enrichUser = (user) => {
        const extra = directoryByUid.get(user.uid) || {};
        return {
            ...user,
            nombre: user.nombre || extra.nombre,
            photoURL: user.photoURL || extra.photoURL,
            empresa: user.empresa || extra.empresa,
            correo: extra.correo,
            area: extra.area,
            rol: extra.rol,
        };
    };

    useEffect(() => {
        if (view !== 'general') return; // Only fetch general stats if in the general view

        const fetchData = async () => {
            setLoading(true);
            try {
                const queryParams = new URLSearchParams();
                if (filters.empresa) queryParams.append('empresa', filters.empresa);
                if (filters.area) queryParams.append('area', filters.area);
                if (filters.startDate) queryParams.append('startDate', filters.startDate);
                if (filters.endDate) queryParams.append('endDate', filters.endDate);
                const queryString = queryParams.toString();

                const [loginsRes, topUsersRes] = await Promise.all([
                    fetchWithAuth(`${API_URL}/api/stats/users/logins-over-time?${queryString}`),
                    fetchWithAuth(`${API_URL}/api/stats/users/top-login-users?${queryString}`),
                ]);

                const loginsData = await loginsRes.json();
                if (loginsData.success) {
                    setLoginStats({
                        timeSeries: loginsData.timeSeries || [],
                        totalLogins: loginsData.totalLogins || 0,
                        uniqueUsersCount: loginsData.uniqueUsersCount || 0,
                    });
                }

                const topUsersData = await topUsersRes.json();
                if (topUsersData.success) {
                    setTopUsers(topUsersData.topUsers || []);
                }
            } catch (error) {
                console.error("Error fetching user statistics data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [fetchWithAuth, API_URL, filters, view]);

    // Lista con KPIs: sirve para Por persona y para los promedios de Generales
    useEffect(() => {
        if (selectedUser) return;
        if (view !== 'user' && view !== 'general') return;

        const fetchUsersWithStats = async () => {
            setLoadingUsersWithStats(true);
            try {
                const res = await fetchWithAuth(`${API_URL}/api/stats/users/all-with-stats`);
                const data = await res.json();
                if (data.success) {
                    setUsersWithStats(data.users || []);
                } else {
                    throw new Error(data.error);
                }
            } catch (error) {
                console.error('Error fetching all users with stats:', error);
                alert('No se pudieron cargar las estadísticas de los usuarios.');
            } finally {
                setLoadingUsersWithStats(false);
            }
        };

        fetchUsersWithStats();
    }, [view, selectedUser, fetchWithAuth, API_URL]);

    // Effect to fetch stats for a specific user
    useEffect(() => {
        if (view !== 'user' || !selectedUser) return;

        const fetchUserStats = async () => {
            setLoadingUserStats(true);
            setUserSpecificStats(null);
            try {
                const res = await fetchWithAuth(`${API_URL}/api/stats/users/${selectedUser.uid}`);
                const data = await res.json();
                if (data.success) {
                    setUserSpecificStats(data.stats);
                } else {
                    throw new Error(data.error);
                }
            } catch (error) {
                console.error(`Error fetching stats for user ${selectedUser.uid}:`, error);
                alert(`No se pudieron cargar las estadísticas para ${selectedUser.nombre}.`);
            } finally {
                setLoadingUserStats(false);
            }
        };
        fetchUserStats();
    }, [selectedUser, view, fetchWithAuth, API_URL]);

    const handleKpiClick = async (kpiType, userId, userName) => {
        if (!userId) return;
        setLoadingModal(true);
        setModalData({ title: `Detalle para ${userName}`, columns: [], data: [] });

        let endpoint = '';
        let title = '';
        let columns = [];
        let dataProcessor = data => data;

        switch (kpiType) {
            case 'logins':
                endpoint = `${API_URL}/api/stats/users/${userId}/logins`;
                title = 'Historial de Inicios de Sesión';
                columns = [
                    { key: 'timestamp', header: 'Fecha', isDate: true },
                    { key: 'empresa', header: 'Empresa' },
                    { key: 'area', header: 'Área' },
                ];
                break;
            case 'assigned':
                endpoint = `${API_URL}/api/stats/users/${userId}/responsibilities`;
                title = 'Reportes Asignados como Responsable';
                columns = [
                    { key: 'reporteNombre', header: 'Reporte' },
                    { key: 'area', header: 'Área Asignada' },
                    { key: 'approved', header: 'Aprobado' },
                ];
                dataProcessor = data => data.map(item => ({ ...item, approved: item.approved ? 'Sí' : 'No' }));
                break;
            case 'approved':
                endpoint = `${API_URL}/api/stats/users/${userId}/responsibilities`;
                title = 'Reportes Aprobados';
                columns = [
                    { key: 'reporteNombre', header: 'Reporte' },
                    { key: 'area', header: 'Área' },
                    { key: 'approvalTimestamp', header: 'Fecha de Aprobación', isDate: true },
                ];
                dataProcessor = data => data.filter(item => item.approved);
                break;
            case 'pending':
                endpoint = `${API_URL}/api/stats/users/${userId}/responsibilities`;
                title = 'Reportes Pendientes de Aprobación';
                columns = [ { key: 'reporteNombre', header: 'Reporte' }, { key: 'area', header: 'Área' } ];
                dataProcessor = data => data.filter(item => !item.approved);
                break;
            case 'late':
                endpoint = `${API_URL}/api/stats/users/${userId}/late-approvals`;
                title = 'Aprobaciones Tardías';
                columns = [ { key: 'reportName', header: 'Reporte' }, { key: 'approvalDeadline', header: 'Deadline', isDate: true }, { key: 'approvalTimestamp', header: 'Fecha de Aprobación', isDate: true } ];
                break;
            case 'false':
                endpoint = `${API_URL}/api/stats/users/${userId}/false-approvals`;
                title = 'Aprobaciones en Falso (Revertidas)';
                columns = [ { key: 'reportName', header: 'Reporte' }, { key: 'revertedAt', header: 'Fecha de Reversión', isDate: true } ];
                break;
            case 'adjAssigned':
            case 'adjPending':
            case 'adjOpen':
            case 'adjIgnored':
                endpoint = `${API_URL}/api/stats/users/${userId}/adjustments-assigned`;
                title = kpiType === 'adjIgnored'
                    ? 'Ajustes ignorados (>3 días)'
                    : kpiType === 'adjPending'
                        ? 'Ajustes pendientes'
                        : kpiType === 'adjOpen'
                            ? 'Ajustes abiertos'
                            : 'Ajustes asignados';
                columns = [
                    { key: 'reportName', header: 'Reporte' },
                    { key: 'text', header: 'Ajuste' },
                    { key: 'status', header: 'Estado' },
                    { key: 'assignedAt', header: 'Asignado', isDate: true },
                    { key: 'ignored', header: 'Ignorado' },
                    { key: 'requestedBy', header: 'Solicitó' },
                ];
                dataProcessor = (rows) => {
                    const mapped = rows.map((item) => ({
                        ...item,
                        status: item.status === 'completed' ? 'Por validar' : (item.status === 'validated' ? 'Validado' : 'Pendiente'),
                    }));
                    if (kpiType === 'adjPending') return mapped.filter((item) => item.status === 'Pendiente');
                    if (kpiType === 'adjOpen') return mapped.filter((item) => item.status !== 'Validado');
                    if (kpiType === 'adjIgnored') return mapped.filter((item) => item.ignored === 'Sí');
                    return mapped;
                };
                break;
            default: setLoadingModal(false); setModalData(null); return;
        }

        try {
            const res = await fetchWithAuth(endpoint);
            const result = await res.json();
            if (result.success) setModalData({ title: `${title} para ${userName}`, columns, data: dataProcessor(result.data) });
            else throw new Error(result.error);
        } catch (error) {
            alert(`No se pudieron cargar los detalles: ${error.message}`);
            setModalData(null);
        } finally {
            setLoadingModal(false);
        }
    };

    const generalCohort = useMemo(() => {
        return (usersWithStats || []).map(enrichUser).filter((user) => {
            if (filters.empresa && user.empresa !== filters.empresa) return false;
            if (filters.area && user.area !== filters.area) return false;
            return true;
        });
    }, [usersWithStats, filters.empresa, filters.area, directoryByUid]);

    const generalAverages = useMemo(() => {
        const people = generalCohort;
        const withAssignments = people.filter((user) => (user.reportsAssigned || 0) > 0);
        const withLogin = people.filter((user) => lastLoginMs(user.lastLogin) != null);
        const now = Date.now();
        const totalAssigned = people.reduce((sum, user) => sum + (user.reportsAssigned || 0), 0);
        const totalApproved = people.reduce((sum, user) => sum + (user.reportsApproved || 0), 0);
        const totalPending = people.reduce((sum, user) => sum + (user.reportsPending || 0), 0);
        const totalLate = people.reduce((sum, user) => sum + (user.lateApprovalCount || 0), 0);
        const totalFalse = people.reduce((sum, user) => sum + (user.falseApprovalCount || 0), 0);
        const totalLogins = people.reduce((sum, user) => sum + (user.totalLogins || 0), 0);
        const totalAdjAssigned = people.reduce((sum, user) => sum + (user.adjustmentsAssigned || 0), 0);
        const totalAdjIgnored = people.reduce((sum, user) => sum + (user.adjustmentsIgnored || 0), 0);

        return {
            people: people.length,
            logins: mean(people, (user) => user.totalLogins),
            assigned: mean(people, (user) => user.reportsAssigned),
            approved: mean(people, (user) => user.reportsApproved),
            pending: mean(people, (user) => user.reportsPending),
            approvalRate: mean(withAssignments, (user) => user.approvalRate),
            overallRate: totalAssigned > 0 ? (totalApproved / totalAssigned) * 100 : 0,
            late: mean(people, (user) => user.lateApprovalCount),
            falseApprovals: mean(people, (user) => user.falseApprovalCount),
            adjustmentsAssigned: mean(people, (user) => user.adjustmentsAssigned),
            adjustmentsIgnored: mean(people, (user) => user.adjustmentsIgnored),
            idleDays: mean(withLogin, (user) => (now - lastLoginMs(user.lastLogin)) / 86400000),
            totals: {
                logins: totalLogins,
                assigned: totalAssigned,
                approved: totalApproved,
                pending: totalPending,
                late: totalLate,
                falseApprovals: totalFalse,
                adjustmentsAssigned: totalAdjAssigned,
                adjustmentsIgnored: totalAdjIgnored,
            },
            withAssignments: withAssignments.length,
        };
    }, [generalCohort]);

    const areaStats = useMemo(() => {
        const people = (usersWithStats || []).map(enrichUser).filter((user) => {
            if (areaFilters.empresa && user.empresa !== areaFilters.empresa) return false;
            if (areaFilters.area && user.area !== areaFilters.area) return false;
            return true;
        });
        return groupByArea(people).sort((a, b) => a.index - b.index);
    }, [usersWithStats, areaFilters.empresa, areaFilters.area, directoryByUid]);

    const areaFilterOptions = useMemo(() => {
        const people = (usersWithStats || []).map(enrichUser).filter((user) => {
            if (areaFilters.empresa && user.empresa !== areaFilters.empresa) return false;
            return true;
        });
        const areas = [...new Set(people.map((user) => user.area).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b, 'es'));
        return areas.length ? areas : (areasList || []);
    }, [usersWithStats, areaFilters.empresa, directoryByUid, areasList]);

    const worstArea = areaStats.find((area) => area.assigned > 0) || areaStats[0] || null;
    const quietestArea = [...areaStats].sort((a, b) => a.avgLogins - b.avgLogins || b.idleDays - a.idleDays)[0] || null;

    const chartData = useMemo(() => {
        const loginsOverTimeChart = {
            labels: loginStats.timeSeries.map((item) => new Date(`${item.date}T00:00:00Z`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', timeZone: 'UTC' })),
            datasets: [{
                label: 'Inicios de sesión por día',
                data: loginStats.timeSeries.map((item) => item.count),
                fill: true,
                backgroundColor: CHART.blueFill,
                borderColor: CHART.blue,
                tension: 0.3,
            }],
        };

        const topUsersChart = {
            labels: topUsers.map((user) => user.userName),
            datasets: [{
                label: 'Inicios de sesión',
                data: topUsers.map((user) => user.count),
                backgroundColor: CHART.pink,
                borderColor: '#c2185b',
                borderWidth: 1,
            }],
        };

        const profileChart = {
            labels: ['Logins', 'Asignados', 'Aprobados', 'Pendientes', 'Tardías', 'Falsas', 'Aj. asignados', 'Aj. ignorados'],
            datasets: [{
                label: 'Promedio por persona',
                data: [
                    generalAverages.logins,
                    generalAverages.assigned,
                    generalAverages.approved,
                    generalAverages.pending,
                    generalAverages.late,
                    generalAverages.falseApprovals,
                    generalAverages.adjustmentsAssigned,
                    generalAverages.adjustmentsIgnored,
                ].map((value) => Number(value.toFixed(2))),
                backgroundColor: [CHART.blue, CHART.purpleFill, CHART.greenFill, CHART.orangeFill, CHART.dangerFill, 'rgba(94, 53, 177, 0.35)', ink.accentSoft, CHART.orange],
                borderWidth: 0,
            }],
        };

        const workflowChart = {
            labels: ['Aprobados', 'Pendientes'],
            datasets: [{
                data: [generalAverages.totals.approved, generalAverages.totals.pending],
                backgroundColor: [CHART.greenFill, CHART.orangeFill],
                borderColor: [CHART.green, CHART.orange],
                borderWidth: 1,
            }],
        };

        const incidentsChart = {
            labels: ['Tardías', 'En falso'],
            datasets: [{
                label: 'Total',
                data: [generalAverages.totals.late, generalAverages.totals.falseApprovals],
                backgroundColor: [CHART.dangerFill, 'rgba(94, 53, 177, 0.45)'],
                borderWidth: 0,
            }],
        };

        const rateBuckets = [
            { label: '0–50%', min: 0, max: 50 },
            { label: '50–80%', min: 50, max: 80 },
            { label: '80–95%', min: 80, max: 95 },
            { label: '95–100%', min: 95, max: 101 },
        ];
        const ratedUsers = generalCohort.filter((user) => (user.reportsAssigned || 0) > 0);
        const rateDistributionChart = {
            labels: rateBuckets.map((bucket) => bucket.label),
            datasets: [{
                label: 'Personas',
                data: rateBuckets.map((bucket) => ratedUsers.filter((user) => user.approvalRate >= bucket.min && user.approvalRate < bucket.max).length),
                backgroundColor: [CHART.dangerFill, CHART.orangeFill, 'rgba(251, 188, 4, 0.7)', CHART.greenFill],
                borderWidth: 0,
            }],
        };

        const companyMap = new Map();
        generalCohort.forEach((user) => {
            const key = user.empresa || 'Sin empresa';
            if (!companyMap.has(key)) companyMap.set(key, []);
            companyMap.get(key).push(user);
        });
        const companies = [...companyMap.entries()]
            .sort((a, b) => b[1].length - a[1].length)
            .slice(0, 8);
        const companyRateChart = {
            labels: companies.map(([name]) => name),
            datasets: [{
                label: '% de aprobación promedio',
                data: companies.map(([, group]) => {
                    const rated = group.filter((user) => (user.reportsAssigned || 0) > 0);
                    return Number(mean(rated, (user) => user.approvalRate).toFixed(1));
                }),
                backgroundColor: CHART.purpleFill,
                borderWidth: 0,
            }],
        };
        const companyLoadChart = {
            labels: companies.map(([name]) => name),
            datasets: [
                {
                    label: 'Asignados prom.',
                    data: companies.map(([, group]) => Number(mean(group, (user) => user.reportsAssigned).toFixed(1))),
                    backgroundColor: CHART.purpleFill,
                },
                {
                    label: 'Pendientes prom.',
                    data: companies.map(([, group]) => Number(mean(group, (user) => user.reportsPending).toFixed(1))),
                    backgroundColor: CHART.orangeFill,
                },
                {
                    label: 'Logins prom.',
                    data: companies.map(([, group]) => Number(mean(group, (user) => user.totalLogins).toFixed(1))),
                    backgroundColor: CHART.blue,
                },
            ],
        };

        const areas = areaStats.slice(0, 12);
        const areaRateChart = {
            labels: areas.map((area) => area.name),
            datasets: [{
                label: '% de aprobación',
                data: areas.map((area) => Number(area.approvalRate.toFixed(1))),
                backgroundColor: areas.map((area) => scoreTone(area.approvalRate)),
                borderWidth: 0,
            }],
        };
        const areaIncidentsChart = {
            labels: areas.map((area) => area.name),
            datasets: [
                {
                    label: 'Tardías',
                    data: areas.map((area) => area.late),
                    backgroundColor: CHART.dangerFill,
                },
                {
                    label: 'En falso',
                    data: areas.map((area) => area.falseApprovals),
                    backgroundColor: 'rgba(94, 53, 177, 0.45)',
                },
            ],
        };
        const areaIndexChart = {
            labels: areas.map((area) => area.name),
            datasets: [{
                label: 'Índice de colaboración',
                data: areas.map((area) => Number(area.index.toFixed(1))),
                backgroundColor: areas.map((area) => scoreTone(area.index)),
                borderWidth: 0,
            }],
        };

        const byLogins = [...areaStats].sort((a, b) => a.totalLogins - b.totalLogins).slice(0, 12);
        const byIdle = [...areaStats].sort((a, b) => b.idleDays - a.idleDays).slice(0, 12);
        const areaLoginChart = {
            labels: byLogins.map((area) => area.name),
            datasets: [{
                label: 'Inicios de sesión',
                data: byLogins.map((area) => area.totalLogins),
                backgroundColor: CHART.blue,
                borderWidth: 0,
            }],
        };
        const areaAvgLoginChart = {
            labels: byLogins.map((area) => area.name),
            datasets: [{
                label: 'Logins promedio',
                data: byLogins.map((area) => Number(area.avgLogins.toFixed(1))),
                backgroundColor: CHART.purpleFill,
                borderWidth: 0,
            }],
        };
        const areaIdleChart = {
            labels: byIdle.map((area) => area.name),
            datasets: [{
                label: 'Días sin acceso',
                data: byIdle.map((area) => Number(area.idleDays.toFixed(1))),
                backgroundColor: byIdle.map((area) => (area.idleDays >= 14 ? CHART.dangerFill : area.idleDays >= 7 ? CHART.orangeFill : CHART.greenFill)),
                borderWidth: 0,
            }],
        };
        const loginShare = [...areaStats].sort((a, b) => b.totalLogins - a.totalLogins);
        const topShare = loginShare.slice(0, 7);
        const restLogins = loginShare.slice(7).reduce((sum, area) => sum + area.totalLogins, 0);
        const areaLoginShareChart = {
            labels: [...topShare.map((area) => area.name), ...(restLogins ? ['Otras'] : [])],
            datasets: [{
                data: [...topShare.map((area) => area.totalLogins), ...(restLogins ? [restLogins] : [])],
                backgroundColor: [CHART.blue, CHART.purpleFill, CHART.greenFill, CHART.orangeFill, CHART.pink, CHART.dangerFill, 'rgba(94, 53, 177, 0.35)', ink.line],
                borderWidth: 0,
            }],
        };

        return {
            loginsOverTimeChart,
            topUsersChart,
            profileChart,
            workflowChart,
            incidentsChart,
            rateDistributionChart,
            companyRateChart,
            companyLoadChart,
            areaRateChart,
            areaIncidentsChart,
            areaIndexChart,
            areaLoginChart,
            areaAvgLoginChart,
            areaIdleChart,
            areaLoginShareChart,
        };
    }, [loginStats, topUsers, generalCohort, generalAverages, areaStats]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: 'bottom' },
        },
        scales: {
            y: { beginAtZero: true, ticks: { precision: 0 }, grace: '10%' },
            x: { beginAtZero: true, grace: '10%' },
        },
    };

    const barOptions = {
        ...chartOptions,
        plugins: { ...chartOptions.plugins, legend: { display: false } },
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
            legend: { display: true, position: 'bottom' },
            customDataLabels: false,
        },
    };

    const percentBarOptions = {
        ...barOptions,
        indexAxis: 'y',
        scales: {
            x: { beginAtZero: true, max: 100, ticks: { callback: (value) => `${value}%` }, grace: '5%' },
            y: { beginAtZero: true },
        },
    };

    const groupedHBarOptions = {
        ...chartOptions,
        indexAxis: 'y',
        scales: {
            x: { beginAtZero: true, ticks: { precision: 0 }, grace: '10%' },
            y: { beginAtZero: true },
        },
    };

    const areaChartHeight = Math.max(260, Math.min(areaStats.length, 12) * 36 + 48);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedAndFilteredUsers = useMemo(() => {
        if (!usersWithStats) return [];
        const query = userFilters.searchTerm.trim().toLowerCase();

        let sortableItems = usersWithStats.map(enrichUser).filter((user) => {
            const matchesCompany = userFilters.empresa ? user.empresa === userFilters.empresa : true;
            if (!matchesCompany) return false;
            if (!query) return true;
            return [user.nombre, user.correo, user.empresa, user.area]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query));
        });

        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key];
                const valB = b[sortConfig.key];
                let comparison = 0;

                if (valA === null || typeof valA === 'undefined') return 1;
                if (valB === null || typeof valB === 'undefined') return -1;

                if (sortConfig.key === 'lastLogin') comparison = (valA?._seconds || 0) - (valB?._seconds || 0);
                else if (typeof valA === 'string') comparison = valA.localeCompare(valB, 'es', { sensitivity: 'base' });
                else comparison = valA - valB;
                
                return sortConfig.direction === 'descending' ? comparison * -1 : comparison;
            });
        }
        return sortableItems;
    }, [usersWithStats, userFilters, sortConfig, directoryByUid]);

    const renderChart = (title, chartComponent, { busy, hint, tall } = {}) => (
        <div style={studio.card}>
            <h4 style={ui.chartTitle}>{title}</h4>
            {hint && <p style={{ ...studio.hint, margin: '-8px 0 14px' }}>{hint}</p>}
            {busy ? <p style={studio.hint}>Cargando…</p> : (
                <div style={{ height: tall || 260 }}>{chartComponent}</div>
            )}
        </div>
    );

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    const KpiCard = ({ title, value, onClick, color, kpiKey, hint }) => {
        const isHovered = hoveredKpi === kpiKey;
        const isClickable = !!onClick;
        return (
            <div
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? onClick : undefined}
                onKeyDown={(event) => {
                    if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onClick();
                    }
                }}
                onMouseEnter={() => isClickable && setHoveredKpi(kpiKey)}
                onMouseLeave={() => setHoveredKpi(null)}
                style={{
                    ...studio.stat,
                    minWidth: 0,
                    cursor: isClickable ? 'pointer' : 'default',
                    ...(isClickable && isHovered ? { borderColor: ink.accent, backgroundColor: ink.accentSoft } : {}),
                }}
            >
                <span style={studio.statLabel}>{title}</span>
                <span style={{ ...studio.statValue, color: color || ink.text }}>{value}</span>
                {(hint || isClickable) && (
                    <span style={{ ...studio.hint, marginTop: 6 }}>{hint || 'Ver detalle'}</span>
                )}
            </div>
        );
    };

    const sortTh = (key, label, align) => (
        <th
            style={{
                ...studio.th,
                ...ui.stickyTh,
                cursor: 'pointer',
                textAlign: align || 'left',
                userSelect: 'none',
                color: sortConfig.key === key ? ink.accentText : ink.faint,
            }}
            onClick={() => requestSort(key)}
        >
            {label}{getSortIndicator(key)}
        </th>
    );

    return (
        <div style={studio.page}>
            <StatsDetailModal
                show={!!modalData}
                loading={loadingModal}
                onClose={() => { setModalData(null); setLoadingModal(false); }}
                title={modalData?.title || ''}
                data={modalData?.data || []}
                columns={modalData?.columns || []}
            />
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Estadísticas</p>
                    <h2 style={studio.title}>
                        {selectedUser ? selectedUser.nombre : 'Actividad de usuarios'}
                    </h2>
                    <p style={studio.subtitle}>
                        {selectedUser
                            ? `${selectedUser.empresa || 'Sin empresa'}${selectedUser.area ? ` · ${selectedUser.area}` : ''}`
                            : 'Inicios de sesión y aprobación, en conjunto o por persona.'}
                    </p>
                </div>
                <div style={studio.stats}>
                    {view === 'user' && !selectedUser && (
                        <div style={studio.stat}>
                            <span style={studio.statValue}>{sortedAndFilteredUsers.length}</span>
                            <span style={studio.statLabel}>Personas</span>
                        </div>
                    )}
                    {view === 'general' && (
                        <>
                            <div style={studio.stat}>
                                <span style={studio.statValue}>{generalAverages.people}</span>
                                <span style={studio.statLabel}>Personas</span>
                            </div>
                            <div style={studio.stat}>
                                <span style={studio.statValue}>{loginStats.totalLogins}</span>
                                <span style={studio.statLabel}>Logins</span>
                            </div>
                            <div style={studio.stat}>
                                <span style={{ ...studio.statValue, color: rateTone(generalAverages.approvalRate).color }}>
                                    {fmtAvg(generalAverages.approvalRate)}%
                                </span>
                                <span style={studio.statLabel}>% prom.</span>
                            </div>
                            {worstArea && (
                                <div style={{ ...studio.stat, borderLeftColor: ink.danger }}>
                                    <span style={{ ...studio.statValue, fontSize: '1.05rem', color: ink.danger }}>
                                        {worstArea.name}
                                    </span>
                                    <span style={studio.statLabel}>Peor área · {fmtAvg(worstArea.index)}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {!selectedUser && (
                <StudioTabs
                    items={[
                        { id: 'general', label: 'Generales' },
                        { id: 'user', label: 'Por persona' },
                    ]}
                    value={view}
                    onChange={(id) => { setView(id); setSelectedUser(null); }}
                />
            )}

            {view === 'general' && (
                <>
                    {/* Filtros */}
                    <FilterDock>
                        <div style={studio.filterPills}>
                            <FilterPill value={filters.empresa} onChange={(value) => setFilters((prev) => ({ ...prev, empresa: value }))} emptyLabel="Empresa">
                                {companyList.map((c) => <option key={c} value={c}>{c}</option>)}
                            </FilterPill>
                            <FilterPill value={filters.area} onChange={(value) => setFilters((prev) => ({ ...prev, area: value }))} emptyLabel="Área">
                                {(areasList || []).map((a) => <option key={a} value={a}>{a}</option>)}
                            </FilterPill>
                            <FilterDate value={filters.startDate} onChange={(value) => setFilters((prev) => ({ ...prev, startDate: value }))} title="Desde" />
                            <FilterDate value={filters.endDate} onChange={(value) => setFilters((prev) => ({ ...prev, endDate: value }))} title="Hasta" />
                        </div>
                        <FilterMeta
                            summary="Empresa y área recortan promedios y gráficas. Las fechas solo aplican a inicios de sesión."
                            chips={[
                                filters.empresa && { key: 'e', label: filters.empresa, onClear: () => setFilters((prev) => ({ ...prev, empresa: '' })) },
                                filters.area && { key: 'a', label: filters.area, onClear: () => setFilters((prev) => ({ ...prev, area: '' })) },
                                filters.startDate && { key: 'from', label: `Desde ${filters.startDate}`, onClear: () => setFilters((prev) => ({ ...prev, startDate: '' })) },
                                filters.endDate && { key: 'to', label: `Hasta ${filters.endDate}`, onClear: () => setFilters((prev) => ({ ...prev, endDate: '' })) },
                            ].filter(Boolean)}
                            onClearAll={() => setFilters({ empresa: '', area: '', startDate: '', endDate: '' })}
                        />
                    </FilterDock>

                    <div style={ui.kpiGrid}>
                        <KpiCard
                            kpiKey="g-logins"
                            title="Logins promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.logins)}
                            hint={`${generalAverages.totals.logins} en total`}
                        />
                        <KpiCard
                            kpiKey="g-assigned"
                            title="Asignados promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.assigned)}
                            hint={`${generalAverages.totals.assigned} reportes`}
                        />
                        <KpiCard
                            kpiKey="g-approved"
                            title="Aprobados promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.approved)}
                            hint={`${generalAverages.totals.approved} aprobados`}
                        />
                        <KpiCard
                            kpiKey="g-pending"
                            title="Pendientes promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.pending)}
                            color={generalAverages.pending > 0 ? ink.danger : undefined}
                            hint={`${generalAverages.totals.pending} pendientes`}
                        />
                        <KpiCard
                            kpiKey="g-rate"
                            title="% de aprobación promedio"
                            value={loadingUsersWithStats ? '…' : `${fmtAvg(generalAverages.approvalRate)}%`}
                            color={rateTone(generalAverages.approvalRate).color}
                            hint={`Global ${fmtAvg(generalAverages.overallRate)}% · ${generalAverages.withAssignments} con carga`}
                        />
                        <KpiCard
                            kpiKey="g-late"
                            title="Tardías promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.late)}
                            color={generalAverages.late > 0 ? ink.danger : undefined}
                            hint={`${generalAverages.totals.late} tardías`}
                        />
                        <KpiCard
                            kpiKey="g-false"
                            title="En falso promedio"
                            value={loadingUsersWithStats ? '…' : fmtAvg(generalAverages.falseApprovals)}
                            color={generalAverages.falseApprovals > 0 ? ink.danger : undefined}
                            hint={`${generalAverages.totals.falseApprovals} revertidas`}
                        />
                        <KpiCard
                            kpiKey="g-idle"
                            title="Inactividad media"
                            value={loadingUsersWithStats ? '…' : `${fmtAvg(generalAverages.idleDays)} d`}
                            hint="Días desde el último acceso"
                        />
                    </div>

                    {renderChart(
                        'Inicios de sesión en el tiempo',
                        <Line options={chartOptions} data={chartData.loginsOverTimeChart} />,
                        { busy: loading, hint: 'Serie temporal: cómo se mueve el acceso día a día.', tall: 280 }
                    )}
                    <div style={ui.chartGrid}>
                        {renderChart(
                            'Top 5 por inicios de sesión',
                            <Bar options={{ ...barOptions, indexAxis: 'y' }} data={chartData.topUsersChart} />,
                            { busy: loading, hint: 'Barras horizontales: ranking de actividad.' }
                        )}
                        {renderChart(
                            'Perfil promedio por persona',
                            <Bar options={barOptions} data={chartData.profileChart} />,
                            { busy: loadingUsersWithStats, hint: 'Barras: compara los promedios entre sí.' }
                        )}
                        {renderChart(
                            'Estado de aprobaciones',
                            <Doughnut options={doughnutOptions} data={chartData.workflowChart} />,
                            { busy: loadingUsersWithStats, hint: 'Dona: peso de aprobados frente a pendientes.' }
                        )}
                        {renderChart(
                            'Incidencias de aprobación',
                            <Bar options={barOptions} data={chartData.incidentsChart} />,
                            { busy: loadingUsersWithStats, hint: 'Barras: tardías vs. revertidas (totales).' }
                        )}
                        {renderChart(
                            'Distribución del % de aprobación',
                            <Bar options={barOptions} data={chartData.rateDistributionChart} />,
                            { busy: loadingUsersWithStats, hint: 'Histograma: cuántas personas caen en cada rango.' }
                        )}
                        {renderChart(
                            '% de aprobación por empresa',
                            <Bar options={{ ...barOptions, indexAxis: 'y' }} data={chartData.companyRateChart} />,
                            { busy: loadingUsersWithStats, hint: 'Ranking: promedio de quienes tienen reportes asignados.' }
                        )}
                    </div>
                    {renderChart(
                        'Carga promedio por empresa',
                        <Bar options={chartOptions} data={chartData.companyLoadChart} />,
                        { busy: loadingUsersWithStats, hint: 'Barras agrupadas: logins, asignados y pendientes por empresa.', tall: 300 }
                    )}

                    <div style={ui.sectionHead}>
                        <div>
                            <p style={studio.kicker}>Por área</p>
                            <h3 style={{ ...studio.formTitle, margin: '4px 0 0' }}>Colaboración, responsabilidad y acceso</h3>
                            <p style={{ ...studio.hint, marginTop: 6 }}>
                                Índice = 50% aprobación + 20% puntualidad + 20% integridad + 10% cierre de pendientes.
                                Filtra empresa y área de este bloque sin cambiar el resto de Generales.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {worstArea && (
                                <div style={{ ...studio.stat, borderLeftColor: ink.danger, minWidth: 200 }}>
                                    <span style={studio.statLabel}>Peor índice</span>
                                    <span style={{ ...studio.statValue, fontSize: '1.15rem', color: ink.danger }}>{worstArea.name}</span>
                                    <span style={{ ...studio.hint, marginTop: 6 }}>
                                        {fmtAvg(worstArea.index)} / 100 · {fmtAvg(worstArea.approvalRate)}% aprobación
                                    </span>
                                </div>
                            )}
                            {quietestArea && (
                                <div style={{ ...studio.stat, borderLeftColor: CHART.blue, minWidth: 200 }}>
                                    <span style={studio.statLabel}>Menos activa</span>
                                    <span style={{ ...studio.statValue, fontSize: '1.15rem', color: CHART.blue }}>{quietestArea.name}</span>
                                    <span style={{ ...studio.hint, marginTop: 6 }}>
                                        {fmtAvg(quietestArea.avgLogins)} logins/persona · {fmtAvg(quietestArea.idleDays)} d inactiva
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <FilterDock>
                        <div style={studio.filterPills}>
                            <FilterPill
                                value={areaFilters.empresa}
                                onChange={(value) => setAreaFilters({ empresa: value, area: '' })}
                                emptyLabel="Empresa"
                            >
                                {companyList.map((company) => <option key={company} value={company}>{company}</option>)}
                            </FilterPill>
                            <FilterPill
                                value={areaFilters.area}
                                onChange={(value) => setAreaFilters((prev) => ({ ...prev, area: value }))}
                                emptyLabel="Área"
                            >
                                {areaFilterOptions.map((area) => <option key={area} value={area}>{area}</option>)}
                            </FilterPill>
                        </div>
                        <FilterMeta
                            summary={`${areaStats.length} ${areaStats.length === 1 ? 'área' : 'áreas'} en el corte`}
                            chips={[
                                areaFilters.empresa && { key: 'ae', label: areaFilters.empresa, onClear: () => setAreaFilters({ empresa: '', area: '' }) },
                                areaFilters.area && { key: 'aa', label: areaFilters.area, onClear: () => setAreaFilters((prev) => ({ ...prev, area: '' })) },
                            ].filter(Boolean)}
                            onClearAll={() => setAreaFilters({ empresa: '', area: '' })}
                        />
                    </FilterDock>
                    <div style={ui.chartGrid}>
                        {renderChart(
                            '% de aprobación por área',
                            <Bar options={percentBarOptions} data={chartData.areaRateChart} />,
                            { busy: loadingUsersWithStats, hint: 'Barras horizontales: peor índice arriba. Color según el %.', tall: areaChartHeight }
                        )}
                        {renderChart(
                            'Tardías y en falso por área',
                            <Bar options={groupedHBarOptions} data={chartData.areaIncidentsChart} />,
                            { busy: loadingUsersWithStats, hint: 'Barras agrupadas: incidencias de puntualidad e integridad.', tall: areaChartHeight }
                        )}
                    </div>
                    {renderChart(
                        'Índice de colaboración y responsabilidad',
                        <Bar options={percentBarOptions} data={chartData.areaIndexChart} />,
                        { busy: loadingUsersWithStats, hint: 'Peor área arriba. Rojo bajo 60, naranja 60–80, verde 80 o más.', tall: areaChartHeight }
                    )}
                    <div style={ui.chartGrid}>
                        {renderChart(
                            'Inicios de sesión por área',
                            <Bar options={groupedHBarOptions} data={chartData.areaLoginChart} />,
                            { busy: loadingUsersWithStats, hint: 'Totales. Menos acceso arriba.', tall: areaChartHeight }
                        )}
                        {renderChart(
                            'Logins promedio por persona',
                            <Bar options={groupedHBarOptions} data={chartData.areaAvgLoginChart} />,
                            { busy: loadingUsersWithStats, hint: 'Actividad media de cada área.', tall: areaChartHeight }
                        )}
                        {renderChart(
                            'Inactividad media por área',
                            <Bar options={groupedHBarOptions} data={chartData.areaIdleChart} />,
                            { busy: loadingUsersWithStats, hint: 'Días desde el último acceso. Más inactiva arriba.', tall: areaChartHeight }
                        )}
                        {renderChart(
                            'Participación de inicios de sesión',
                            <Doughnut options={doughnutOptions} data={chartData.areaLoginShareChart} />,
                            { busy: loadingUsersWithStats, hint: 'Dona: qué área concentra el acceso.' }
                        )}
                    </div>
                </>
            )}

            {view === 'user' && !selectedUser && (
                <div style={studio.card}>
                    <FilterDock>
                        <SearchField
                            value={userFilters.searchTerm}
                            onChange={(value) => setUserFilters((prev) => ({ ...prev, searchTerm: value }))}
                            placeholder="Buscar por nombre, correo o área…"
                        />
                        <div style={studio.filterPills}>
                            <FilterPill
                                value={userFilters.empresa}
                                onChange={(value) => setUserFilters((prev) => ({ ...prev, empresa: value }))}
                                emptyLabel="Empresa"
                            >
                                {companyList.map((c) => <option key={c} value={c}>{c}</option>)}
                            </FilterPill>
                        </div>
                        <FilterMeta
                            summary={`${sortedAndFilteredUsers.length} ${sortedAndFilteredUsers.length === 1 ? 'persona' : 'personas'}`}
                            chips={[
                                userFilters.searchTerm && { key: 'q', label: `“${userFilters.searchTerm}”`, onClear: () => setUserFilters((prev) => ({ ...prev, searchTerm: '' })) },
                                userFilters.empresa && { key: 'e', label: userFilters.empresa, onClear: () => setUserFilters((prev) => ({ ...prev, empresa: '' })) },
                            ].filter(Boolean)}
                            onClearAll={() => setUserFilters({ empresa: '', searchTerm: '' })}
                        />
                    </FilterDock>
                    {loadingUsersWithStats ? (
                        <div style={studio.empty}>Cargando personas…</div>
                    ) : sortedAndFilteredUsers.length === 0 ? (
                        <div style={studio.empty}>Ninguna persona coincide con los filtros.</div>
                    ) : (
                        <div style={ui.tableWrap}>
                            <table style={studio.table}>
                                <thead>
                                    <tr>
                                        {sortTh('nombre', 'Persona')}
                                        {sortTh('empresa', 'Empresa')}
                                        {sortTh('totalLogins', 'Logins', 'right')}
                                        {sortTh('lastLogin', 'Último acceso')}
                                        {sortTh('reportsAssigned', 'Asignados', 'right')}
                                        {sortTh('reportsApproved', 'Aprobados', 'right')}
                                        {sortTh('reportsPending', 'Pendientes', 'right')}
                                        {sortTh('approvalRate', '% Aprob.', 'right')}
                                        {sortTh('lateApprovalCount', 'Tardías', 'right')}
                                        {sortTh('falseApprovalCount', 'Falsas', 'right')}
                                        {sortTh('adjustmentsAssigned', 'Aj. asig.', 'right')}
                                        {sortTh('adjustmentsPending', 'Aj. pend.', 'right')}
                                        {sortTh('adjustmentsOpen', 'Aj. abiertos', 'right')}
                                        {sortTh('adjustmentsIgnored', 'Aj. ignor.', 'right')}
                                        {sortTh('adjustmentResolveRate', '% aj. res.', 'right')}
                                        <th style={{ ...studio.th, ...ui.stickyTh }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAndFilteredUsers.map((user) => (
                                        <tr
                                            key={user.uid}
                                            style={{
                                                ...(hoveredRow === user.uid ? studio.trHover : {}),
                                                cursor: 'pointer',
                                            }}
                                            onMouseEnter={() => setHoveredRow(user.uid)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            onClick={() => setSelectedUser(user)}
                                        >
                                            <td style={studio.td}>
                                                <div style={ui.person}>
                                                    <img
                                                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.nombre || '?')}&background=5E35B1&color=fff&size=72`}
                                                        alt=""
                                                        style={studio.avatar}
                                                    />
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={ui.personName}>{user.nombre || 'Sin nombre'}</div>
                                                        {(user.correo || user.area) && (
                                                            <div style={ui.personMeta}>
                                                                {user.correo || user.area}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ ...studio.td, color: ink.muted }}>
                                                <div>{user.empresa || '—'}</div>
                                                {user.area && user.correo && <div style={ui.personMeta}>{user.area}</div>}
                                            </td>
                                            <td style={ui.num}>{user.totalLogins}</td>
                                            <td style={{ ...studio.td, color: ink.muted, whiteSpace: 'nowrap' }}>{formatTimestamp(user.lastLogin)}</td>
                                            <td style={ui.num}>{user.reportsAssigned}</td>
                                            <td style={ui.num}>{user.reportsApproved}</td>
                                            <td style={ui.num}>
                                                <CountChip value={user.reportsPending} warn={user.reportsPending > 0} />
                                            </td>
                                            <td style={ui.num}><RateChip value={user.approvalRate} /></td>
                                            <td style={ui.num}><CountChip value={user.lateApprovalCount} warn /></td>
                                            <td style={ui.num}><CountChip value={user.falseApprovalCount} warn /></td>
                                            <td style={ui.num}>{user.adjustmentsAssigned || 0}</td>
                                            <td style={ui.num}><CountChip value={user.adjustmentsPending || 0} warn /></td>
                                            <td style={ui.num}><CountChip value={user.adjustmentsOpen || 0} warn /></td>
                                            <td style={ui.num}><CountChip value={user.adjustmentsIgnored || 0} warn /></td>
                                            <td style={ui.num}><RateChip value={user.adjustmentResolveRate || 0} /></td>
                                            <td style={studio.td}>
                                                <button
                                                    type="button"
                                                    style={ui.rowBtn}
                                                    onClick={(event) => { event.stopPropagation(); setSelectedUser(user); }}
                                                >
                                                    Ver
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {view === 'user' && selectedUser && (
                <div style={studio.card}>
                    <div style={ui.detailHead}>
                        <div style={ui.person}>
                            <img
                                src={selectedUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.nombre || '?')}&background=5E35B1&color=fff&size=96`}
                                alt=""
                                style={{ ...studio.avatar, width: 56, height: 56 }}
                            />
                            <div>
                                <div style={studio.crumbBar}>
                                    <button type="button" onClick={() => setSelectedUser(null)} style={studio.crumbBtn}>Por persona</button>
                                    <span style={studio.crumbSep}>/</span>
                                    <span style={studio.crumbHere}>{selectedUser.nombre}</span>
                                </div>
                                <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>{selectedUser.nombre}</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                    {selectedUser.rol && <RoleBadge roleId={selectedUser.rol} />}
                                    {selectedUser.correo && <span style={{ fontSize: '0.82rem', color: ink.muted }}>{selectedUser.correo}</span>}
                                </div>
                            </div>
                        </div>
                        <button type="button" onClick={() => setSelectedUser(null)} style={studio.ghostBtn}>‹ Volver a la lista</button>
                    </div>
                    <div style={ui.factGrid}>
                        <div style={studio.stat}>
                            <span style={studio.statLabel}>Empresa</span>
                            <span style={{ ...studio.statValue, fontSize: '1.05rem' }}>{selectedUser.empresa || '—'}</span>
                        </div>
                        <div style={studio.stat}>
                            <span style={studio.statLabel}>Área</span>
                            <span style={{ ...studio.statValue, fontSize: '1.05rem' }}>{selectedUser.area || '—'}</span>
                        </div>
                        <div style={studio.stat}>
                            <span style={studio.statLabel}>Último acceso</span>
                            <span style={{ ...studio.statValue, fontSize: '1.05rem' }}>
                                {formatTimestamp(userSpecificStats?.lastLogin || selectedUser.lastLogin)}
                            </span>
                        </div>
                    </div>
                    {loadingUserStats ? (
                        <div style={studio.empty}>Cargando detalle…</div>
                    ) : userSpecificStats && (
                        <div style={ui.kpiGrid}>
                            <KpiCard kpiKey="logins" title="Inicios de sesión" value={userSpecificStats.totalLogins} onClick={() => handleKpiClick('logins', selectedUser.uid, selectedUser.nombre)} />
                            <KpiCard kpiKey="assigned" title="Reportes asignados" value={userSpecificStats.reportsAssigned} onClick={() => handleKpiClick('assigned', selectedUser.uid, selectedUser.nombre)} />
                            <KpiCard kpiKey="approved" title="Aprobados" value={userSpecificStats.reportsApproved} onClick={() => handleKpiClick('approved', selectedUser.uid, selectedUser.nombre)} />
                            <KpiCard kpiKey="pending" title="Pendientes" value={userSpecificStats.reportsPending} onClick={() => handleKpiClick('pending', selectedUser.uid, selectedUser.nombre)} color={userSpecificStats.reportsPending > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="approvalRate" title="% de aprobación" value={`${userSpecificStats.approvalRate.toFixed(1)}%`} color={rateTone(userSpecificStats.approvalRate).color} />
                            <KpiCard kpiKey="late" title="Aprobaciones tardías" value={userSpecificStats.lateApprovalCount || 0} onClick={() => handleKpiClick('late', selectedUser.uid, selectedUser.nombre)} color={(userSpecificStats.lateApprovalCount || 0) > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="false" title="Aprobaciones en falso" value={userSpecificStats.falseApprovalCount || 0} onClick={() => handleKpiClick('false', selectedUser.uid, selectedUser.nombre)} color={(userSpecificStats.falseApprovalCount || 0) > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="adjAssigned" title="Ajustes asignados" value={userSpecificStats.adjustmentsAssigned || 0} onClick={() => handleKpiClick('adjAssigned', selectedUser.uid, selectedUser.nombre)} />
                            <KpiCard kpiKey="adjPending" title="Ajustes pendientes" value={userSpecificStats.adjustmentsPending || 0} onClick={() => handleKpiClick('adjPending', selectedUser.uid, selectedUser.nombre)} color={(userSpecificStats.adjustmentsPending || 0) > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="adjOpen" title="Ajustes abiertos" value={userSpecificStats.adjustmentsOpen || 0} onClick={() => handleKpiClick('adjOpen', selectedUser.uid, selectedUser.nombre)} color={(userSpecificStats.adjustmentsOpen || 0) > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="adjIgnored" title="Ajustes ignorados (>3 d)" value={userSpecificStats.adjustmentsIgnored || 0} onClick={() => handleKpiClick('adjIgnored', selectedUser.uid, selectedUser.nombre)} color={(userSpecificStats.adjustmentsIgnored || 0) > 0 ? ink.danger : undefined} />
                            <KpiCard kpiKey="adjRate" title="% ajustes resueltos" value={`${Number(userSpecificStats.adjustmentResolveRate || 0).toFixed(1)}%`} color={rateTone(userSpecificStats.adjustmentResolveRate || 0).color} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const ui = {
    tableWrap: {
        overflow: 'auto',
        maxHeight: '68vh',
        border: `1px solid ${ink.line}`,
        borderRadius: 12,
    },
    stickyTh: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    chartTitle: {
        margin: '0 0 16px',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
    },
    person: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
    },
    personName: {
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    personMeta: {
        marginTop: 2,
        fontSize: '0.75rem',
        color: ink.faint,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    num: {
        ...studio.td,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
    },
    chip: {
        display: 'inline-block',
        minWidth: 28,
        textAlign: 'center',
        borderRadius: 999,
        padding: '3px 8px',
        fontSize: '0.75rem',
        fontWeight: 700,
        backgroundColor: ink.wash,
        color: ink.muted,
    },
    rowBtn: {
        ...studio.ghostBtn,
        padding: '6px 10px',
        fontSize: '0.78rem',
    },
    detailHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 18,
        paddingBottom: 16,
        borderBottom: `1px solid ${ink.line}`,
    },
    factGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 10,
        marginBottom: 16,
    },
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 10,
    },
    chartGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap: 12,
    },
    sectionHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 16,
        flexWrap: 'wrap',
        marginTop: 8,
    },
};

export default UserStatisticsPanel;